import { appPath } from './paths';
import type { FilterFieldDef } from './filterMaps';

export interface DashboardFilterContext {
  fromUtc?: string;
  toUtc?: string;
  segmentIds?: string[];
  contactCategoryIds?: number[];
  assigneeIds?: string[];
}

function serializeValue(value: unknown, def: FilterFieldDef): string | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  switch (def.type) {
    case 'string':
      return String(value);
    case 'number':
      return String(value);
    case 'boolean':
      return String(value);
    case 'comma-separated-strings':
      if (Array.isArray(value) && value.length > 0) {
        return value.join(',');
      }
      return undefined;
    case 'comma-separated-numbers':
      if (Array.isArray(value) && value.length > 0) {
        return value.join(',');
      }
      return undefined;
    default:
      return String(value);
  }
}

export function buildListUrl(
  path: string,
  filters: Record<string, unknown>,
  fields: readonly FilterFieldDef[],
): string {
  const params = new URLSearchParams();
  for (const def of fields) {
    const value = filters[def.field];
    const serialized = serializeValue(value, def);
    if (serialized !== undefined) {
      params.set(def.param, serialized);
    }
  }
  const qs = params.toString();
  return qs ? `${appPath(path)}?${qs}` : appPath(path);
}

export function withDashboardContext(
  targetPath: string,
  dashboardFilters: DashboardFilterContext,
  widgetOverrides: Record<string, unknown>,
  targetFields: readonly FilterFieldDef[],
): string {
  const merged: Record<string, unknown> = { ...widgetOverrides };

  const supportedFields = new Set(targetFields.map((f) => f.field));

  if (supportedFields.has('segmentIds') && dashboardFilters.segmentIds?.length) {
    merged.segmentIds = dashboardFilters.segmentIds;
  }
  if (supportedFields.has('contactCategoryIds') && dashboardFilters.contactCategoryIds?.length) {
    merged.contactCategoryIds = dashboardFilters.contactCategoryIds;
  }
  if (supportedFields.has('contactCategoryId') && dashboardFilters.contactCategoryIds?.length) {
    merged.contactCategoryId = dashboardFilters.contactCategoryIds[0];
  }
  if (supportedFields.has('contactCategoryFilter') && dashboardFilters.contactCategoryIds?.length) {
    merged.contactCategoryFilter = dashboardFilters.contactCategoryIds[0];
  }
  if (supportedFields.has('assigneeUserId') && dashboardFilters.assigneeIds?.length) {
    merged.assigneeUserId = dashboardFilters.assigneeIds[0];
  }
  if (supportedFields.has('assigneeFilter') && dashboardFilters.assigneeIds?.length) {
    merged.assigneeFilter = dashboardFilters.assigneeIds[0];
  }
  if (supportedFields.has('ownerUserId') && dashboardFilters.assigneeIds?.length) {
    merged.ownerUserId = dashboardFilters.assigneeIds[0];
  }
  if (supportedFields.has('createdFrom') && dashboardFilters.fromUtc) {
    merged.createdFrom = dashboardFilters.fromUtc;
  }
  if (supportedFields.has('createdTo') && dashboardFilters.toUtc) {
    merged.createdTo = dashboardFilters.toUtc;
  }
  if (supportedFields.has('dueFrom') && dashboardFilters.fromUtc) {
    merged.dueFrom = dashboardFilters.fromUtc;
  }
  if (supportedFields.has('dueTo') && dashboardFilters.toUtc) {
    merged.dueTo = dashboardFilters.toUtc;
  }
  if (supportedFields.has('segmentFilter') && dashboardFilters.segmentIds?.length) {
    merged.segmentFilter = dashboardFilters.segmentIds;
  }

  return buildListUrl(targetPath, merged, targetFields);
}


